import React from "react";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { HiChevronDown } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import AxaoLogo from "../assets/AXAO.svg";
import FoonLogo from "../assets/FOON.svg";
import StareIcon from "../assets/STARE.svg";
import BocceIcon from "../assets/BOCCE.svg";
import YT from "../assets/YT.svg";
import IG from "../assets/IG.svg";
import { buildFoonPath } from "../utils/routes";
import { useCurrencyContext } from "../lib/CurrencyContext";

const menuItems = [
  "Shop All",
  "Earbuds",
  "Headphones",
  "Speakers",
  "Accessories",
  "Account",
  "Support",
  "Community",
];

const logos = [AxaoLogo, FoonLogo, StareIcon, BocceIcon];

const DropdownMenu = ({ isOpen, onClose, isScrolled, isNavbarVisible }) => {
  const navigate = useNavigate();
  const { currency, setCurrency, currencySymbols, supportedCurrencies } =
    useCurrencyContext();

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-40 max-h-[800px] flex justify-start max-w-[1440px] mx-auto overflow-clip"
    >
      <motion.div
        initial={{ y: "-100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "-100%", opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 h-full bg-black/70 max-md:hidden"
        onClick={onClose}
      />

      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="absolute z-50 right-0 top-0 flex h-full w-full max-w-[438px] flex-col bg-white text-black pointer-events-auto"
      >
        <div
          className={`${
            isScrolled && isNavbarVisible ? "mt-16" : "mt-24"
          } h-px w-full bg-[#dddcdc]`}
        />
        <div className="flex flex-1 flex-col justify-between">
          <div className="px-12 pb-8 pt-9">
            <motion.ul
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: { staggerChildren: 0.045, delayChildren: 0.06 },
                },
              }}
              className="space-y-[17px]"
            >
              {menuItems.map((item) => (
                <motion.li
                  key={item}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (item === "Shop All") {
                        navigate(buildFoonPath("/store"));
                      }
                      onClose();
                    }}
                    className="font-['Helvetica_Neue',sans-serif] text-[20px] font-light leading-[1.35] tracking-[-0.03em] text-black transition-transform duration-300 hover:translate-x-1"
                  >
                    {item}
                  </button>
                </motion.li>
              ))}
            </motion.ul>
          </div>

          <div className="">
            <div
              className="px-12 pb-6 pt-4"
              style={{
                borderBottom: "1px solid #dddcdc",
              }}
            >
              <div
                className="flex items-center justify-center gap-[9px] h-10 w-[174px]"
                style={{
                  border: "1px solid #dddcdc",
                }}
              >
                <span className="font-mono text-[12px] font-light uppercase tracking-[0.03em] text-[#6d6d6d]">
                  Currency:
                </span>
                <div className="relative">
                  <select
                    aria-label="Select currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="cursor-pointer appearance-none bg-transparent font-mono text-[12px] font-light uppercase tracking-[0.12px] text-[#6d6d6d] outline-none pr-4"
                  >
                    {supportedCurrencies.map((c) => (
                      <option key={c} value={c}>
                        {currencySymbols[c]} {c}
                      </option>
                    ))}
                  </select>
                  <HiChevronDown className="pointer-events-none absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 text-[#6d6d6d]" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap p-8">
              <motion.img
                src={IG}
                alt="IG logo"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="shrink-0"
                onClick={() =>
                  window.open(
                    "https://www.instagram.com/foon.audio?igsh=MXZubjFrY29vbmpqcg==",
                    "_blank",
                  )
                }
              />
              <motion.img
                src={YT}
                alt="YT logo"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="shrink-0"
              />

              {logos.map((Icon, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                  className="shrink-0"
                >
                  <img
                    src={Icon}
                    alt="Logo"
                    className="h-auto cursor-pointer"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.aside>
    </motion.div>
  );
};

DropdownMenu.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  isScrolled: PropTypes.bool.isRequired,
  isNavbarVisible: PropTypes.bool.isRequired,
};

export default DropdownMenu;
